import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { map, tap, pluck, catchError, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { combineLatest } from 'rxjs/observable/combineLatest';

import {
  PhysicApproval,
  PhysicApprovalProduct,
  PhysicApprovalPrivacyConsents,
  // PhysicApprovalGenericConsents,
  PhysicApprovalConsents,
  PhysicApprovalResponse,
  PhysicApprovalFeedback
} from './alg-fisico.service.d';
import { environment } from '../../environments/environment';

export const APPROVAL_STATUS_OK = 'lead_status_ok';
export const APPROVAL_STATUS_COMPLETED = 'lead_status_completed';
export const APPROVAL_STATUS_SIGNED = 'lead_status_signed';
export const APPROVAL_STATUS_INVALID = 'lead_status_invalid';
export const APPROVAL_STATUS_ERROR = 'lead_status_error';

const defaultPrivacyConsents = {
  valid: false,
  data: {
    privacyComCom: undefined,
    privacyProf: undefined,
    privacyQualServ: undefined,
    privacyComTer: undefined,
    privacyProfTer: undefined
  }
};

const defaultGenericConsents = {
  valid: false,
  data: {
    cteCondition: undefined,
    cfgCondition: undefined,
    cadastralCondition: undefined,
    clauseCondition: undefined,
    consentsCondition: undefined
  }
};

@Injectable()
export class AlgFisicoService implements OnDestroy {

  public lead$: Observable<PhysicApproval>;
  public leadProduct$: Observable<PhysicApprovalProduct>;
  public leadProductName$: Observable<string>;
  public leadStatus$: Observable<string>;
  public isLeadLoading$: Observable<boolean>;
  public isFeedbackLoading$: Observable<boolean>;
  public isConsentsOk$: Observable<boolean>;
  public privacyConsets$: Observable<PhysicApprovalPrivacyConsents>;
  public policiesUrl$: Observable<any>;
  public categoryNumber$: Observable<number>;
  public operatorRatingCode$: Observable<PhysicApprovalFeedback>;
  private privacyConsentsSubject = new BehaviorSubject<{valid: boolean, data: PhysicApprovalPrivacyConsents}>(defaultPrivacyConsents);
  private leadResponseSubject = new ReplaySubject<PhysicApprovalResponse>(1); 
  private leadLoadingSubject = new BehaviorSubject<boolean>(false);
  private feedbackLoadingSubject = new BehaviorSubject<boolean>(false);
  private ngUnsubscribe = new Subject<void>();
  private leadsEndpoint = `${environment.api.basePath}/leads`;
  private physicApprovalEndpoint = `${environment.api.basePath}/physic-approvals`;
  private checkLeadsEndpoint = `${environment.api.basePath}/check-lead`; 
  // private leadsEndpoint = `http://localhost:5000/api/leads`;
  // private physicApprovalEndpoint = `http://localhost:5000/api/physic-approvals`;
  // private checkLeadsEndpoint = `http://localhost:5000/api/check-lead`; 

  constructor(
    private http: HttpClient
  ) {
    this.leadStatus$ = this.leadResponseSubject
      .pipe(
        map((response) => {
          const responseOk = !!response && Math.ceil(response.status / 100) === 2;
          const lead = responseOk && !!response && response.physicApproval;
          let leadStatus = !!lead && !!lead.leadStatus && lead.leadStatus.toLowerCase();
          let completed = lead && lead.completed;
          switch (true) {
            case (leadStatus === 'in lavorazione - privacy confermata' || leadStatus === 'in lavorazione - contattato' || leadStatus === 'in lavorazione - riassegnato') && completed:
              return APPROVAL_STATUS_COMPLETED;
            case (leadStatus=== 'aperto - non contattato') && !completed:
              return APPROVAL_STATUS_OK;
            case leadStatus === 'chiuso - contratto concluso':
              return APPROVAL_STATUS_SIGNED;
            case leadStatus === 'chiuso - contatto bruciato' || leadStatus === 'chiuso - non ricontattare':
              return APPROVAL_STATUS_INVALID;
            default:
              return APPROVAL_STATUS_ERROR;
          }
        }),
      );
    this.isLeadLoading$ = this.leadLoadingSubject
      .pipe(
        distinctUntilChanged()
      );
    this.isFeedbackLoading$ = this.feedbackLoadingSubject
      .pipe(
        distinctUntilChanged()
      );
    this.lead$ = this.leadResponseSubject
      .pipe(
        map((response) => (!!response && response.physicApproval) || null),
      );
    this.leadProduct$ = this.lead$
      .pipe(
        map((lead) => (!!lead && lead.product) || null),
      );
    this.leadProductName$ = this.leadProduct$
      .pipe(
       map((product) => !!product && `${product.family || ''} ${product.type || ''} ${product.duration || ''} ${product.lightRate || ''}`)
      );
    this.isConsentsOk$ = this.privacyConsentsSubject.pipe( map(consentStatus => consentStatus.valid) )
    this.privacyConsets$ = this.privacyConsentsSubject
      .pipe(
        pluck('data')
      );
    this.operatorRatingCode$ = this.lead$
      .pipe(
        map((lead) => (!!lead && lead.operatorRatingCode) || null)
      );
    this.policiesUrl$ = this.lead$
      .pipe(
        map((lead) => ({
          cfgCondition: (!!lead && lead.cfgPolicyUrl) || '',
          cteCondition: (!!lead && lead.ctePolicyUrl) || '',
          generalPolicy: (!!lead && lead.generalPolicyUrl) || ''
        }))
      );
    this.categoryNumber$ = this.lead$
      .pipe(
        map((lead) => !!lead && lead.categoryNumber || null)
      );
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public checkLead(id: string): any {    
    return this.http.get(
      `${this.checkLeadsEndpoint}/${id}`,
      { observe: 'response' }
    )
    .pipe(      
      map((response: HttpResponse<any>) => ({
        status: response.status,
        result: (response.body)
      })),
      catchError((response: HttpResponse<any>) => Observable.of({
        status: response.status,
        result: response.statusText
      }))
    );
  }

  public fetchLead(id: string): void {
    this.leadLoadingSubject.next(true);
    this.getLead(id)
      .pipe(
        tap(() => {
          this.leadLoadingSubject.next(false);
        })
      )
      .subscribe((response) => {
        this.leadResponseSubject.next(response);
      });
  }

  public saveConsents(id, consents: PhysicApprovalConsents, complete: boolean = false) {
    // Set function field to enable Salesforce to compute the lead
    this.leadLoadingSubject.next(true);
    this.saveLead(id, {
        ...consents,
        docPrivacyFirmato: true,
        leadStatus: 'In lavorazione - Privacy Confermata',
      })
      .pipe(
        tap(() => {
          this.leadLoadingSubject.next(false);
        })
      )
      .subscribe((response:any) => {
        const data = {status : response.status, physicApproval: response.lead}
        this.leadResponseSubject.next(data);
      });
  }

  public saveFeedback(leadId, rating) {
    this.feedbackLoadingSubject.next(true);
    this.updatePhysicApproval(
      leadId,
      { operatorRatingCode: rating }
    )
    .pipe(
      tap(() => {
        this.feedbackLoadingSubject.next(false);
      })
    )
    .subscribe((response:any) => {
      this.leadResponseSubject.next(response);
    });
  }

  public updatePrivacyConsents(consents: {valid: boolean, data: PhysicApprovalPrivacyConsents}): void {
    if(!consents.data.privacyComCom) consents.valid = false;
    this.privacyConsentsSubject.next(consents);
  }

  private transformPhysicApproval(lead:any): PhysicApproval {
    const { _id, operatorRatingCode, ...cleanedPhysicApproval } = lead || ({} as PhysicApproval);
    return {
      id: _id,
      operatorRatingCode: parseInt(operatorRatingCode, 10) || null,
      ...cleanedPhysicApproval
    };
  }

  private getLead(id: string): Observable<PhysicApprovalResponse> {
    return this.http.get(
      `${this.leadsEndpoint}/${id}`,
      { observe: 'response' }
    )
    .pipe(
      map((response: HttpResponse<any>) => ({
        status: response.status,
        physicApproval: this.transformPhysicApproval(response.body)
      })),
      catchError((response: HttpResponse<any>) => Observable.of({
        status: response.status,
        physicApproval: null
      }))
    );
  }

  private saveLead(id: string, data: any) {
    return this.http.post(
      `${this.leadsEndpoint}/${id}`,
      data,
      { observe: 'response' }
    )
    .pipe(
      map((response: HttpResponse<any>) => ({
        status: response.status,
        lead: this.transformPhysicApproval(response.body)
      })),
      catchError((response: HttpResponse<any>) => Observable.of({
        status: response.status,
        lead: null
      }))
    );
  }

  private updatePhysicApproval(id:any, data:any) {
    return this.http.put(
      `${this.physicApprovalEndpoint}/${id}`,
      data,
      { observe: 'response', headers: new HttpHeaders({
        'Content-Type': 'application/json; charset=UTF-8',
      }) }
    )
    .pipe(
      map((response: HttpResponse<any>) => ({
        status: response.status,
        physicApproval: !!response.body && this.transformPhysicApproval(response.body['physic approval'])
      })),
      catchError((response: HttpResponse<any>) => Observable.of({
        status: response.status,
        lead: null
      }))
    );
  }

}



// WEBPACK FOOTER //
// ./src/app/services/alg-fisico.service.ts